import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { SelectField } from "../../components/site/FormBits";
import { RES_TYPES, RES_CATS, RES_AUD } from "../PlansEducatifs";
import { api, apiError } from "../../lib/api";

const opts = (o) => Object.entries(o).map(([value, v]) => ({ value, label: typeof v === "string" ? v : v.l }));
const BLANK = { title: "", type: "article", category: "apprendre", audience: "tous", summary: "", url: "", published: true };

const ResourceDialog = ({ res, onClose, onSaved }) => {
  const [d, setD] = useState(res ? { ...BLANK, ...res } : BLANK);
  const set = (k) => (v) => setD((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const save = async () => {
    const body = { title: d.title, type: d.type, category: d.category, audience: d.audience, summary: d.summary, url: d.url, published: d.published };
    try {
      if (res) await api.put(`/admin/resources/${res.id}`, body); else await api.post("/admin/resources", body);
      toast.success("Ressource enregistrée"); onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl bg-[#FFFDFB]" data-testid="resource-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{res ? "Modifier la ressource" : "Nouvelle ressource"}</DialogTitle>
          <DialogDescription>Ajoutez un article, une vidéo, un guide PDF ou une fiche.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <input className="field-input" placeholder="Titre" value={d.title} onChange={set("title")} data-testid="resource-title-input" />
          <div className="grid gap-3 sm:grid-cols-3">
            <SelectField value={d.type} onChange={set("type")} options={opts(RES_TYPES)} testid="resource-type-select" />
            <SelectField value={d.category} onChange={set("category")} options={opts(RES_CATS)} testid="resource-category-select" />
            <SelectField value={d.audience} onChange={set("audience")} options={opts(RES_AUD)} testid="resource-audience-select" />
          </div>
          <textarea rows={3} className="field-input" placeholder="Résumé" value={d.summary} onChange={set("summary")} data-testid="resource-summary-input" />
          <input className="field-input" placeholder="Lien https:// (vidéo, PDF, article)" value={d.url} onChange={set("url")} data-testid="resource-url-input" />
          <label className="flex items-center gap-2 text-sm"><Switch checked={d.published} onCheckedChange={set("published")} data-testid="resource-published-switch" />Publiée</label>
        </div>
        <div className="flex justify-end gap-3"><button onClick={onClose} className="btn-outline !py-2.5">Annuler</button>
          <button onClick={save} className="btn-primary !py-2.5" data-testid="resource-save-button">Enregistrer</button></div>
      </DialogContent>
    </Dialog>
  );
};

export default function ResourcesTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(undefined);
  const load = useCallback(() => { api.get("/admin/resources").then((r) => setItems(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (r) => {
    if (!window.confirm(`Supprimer « ${r.title} »?`)) return;
    await api.delete(`/admin/resources/${r.id}`); load();
  };
  return (
    <div className="space-y-4" data-testid="resources-tab">
      <div className="flex justify-between"><h2 className="text-2xl">Ressources ({items.length})</h2>
        <button onClick={() => setEditing(null)} className="btn-primary !py-2.5" data-testid="resource-new-button"><Plus className="h-4 w-4" />Ajouter</button></div>
      <div className="divide-y divide-[#EDE3D9] rounded-2xl border border-[#EDE3D9] bg-[#FFFDFB]">
        {items.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-4 p-4" data-testid={`admin-resource-${r.id}`}>
            <div><p className="font-medium">{r.title}</p>
              <p className="text-xs text-[#242020]/60">{RES_TYPES[r.type].l} · {RES_CATS[r.category]} · {RES_AUD[r.audience]} · {r.published ? "Publiée" : "Masquée"}{r.is_demo ? " · Démonstration" : ""}</p></div>
            <div className="flex gap-3">
              <button onClick={() => setEditing(r)} aria-label="Modifier" data-testid={`admin-resource-${r.id}-edit`}><Pencil className="h-4 w-4 text-[#650D23]" /></button>
              <button onClick={() => remove(r)} aria-label="Supprimer" data-testid={`admin-resource-${r.id}-delete`}><Trash2 className="h-4 w-4 text-[#b42318]" /></button>
            </div>
          </div>
        ))}
      </div>
      {editing !== undefined && <ResourceDialog res={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />}
    </div>
  );
}
